import { useState, useEffect } from "react";
import { FaUser, FaCamera, FaCheckCircle, FaTimes, FaSpinner, FaMoneyBillWave, FaExclamationTriangle, FaCopy, FaHistory } from 'react-icons/fa';
import Modal from 'react-modal';
import Nav from './nav';

// Set app element for accessibility
Modal.setAppElement('#root');

const Profile = () => {
  const [user, setUser] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    currency: '',
    verificationStatus: 'Not Verified',
    profileImage: null,
    paymentStatus: 'not_completed',
    verificationFee: 0
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    country: '',
    countryCode: 'US'
  });
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  const [idCard, setIdCard] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [currency, setCurrency] = useState('USDT');
  const [network, setNetwork] = useState('ERC20');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [copiedAddress, setCopiedAddress] = useState('');
  const [loadingWallet, setLoadingWallet] = useState(false);
  const [fetchError, setFetchError] = useState('');

  // Currency configuration with networks
  const currencyConfig = {
    BTC: { 
      name: 'Bitcoin', 
      color: 'from-orange-500 to-red-500',
      networks: ['BTC'],
      defaultNetwork: 'BTC'
    },
    USDT: { 
      name: 'Tether', 
      color: 'from-green-500 to-emerald-500',
      networks: ['ERC20', 'TRC20', 'BEP20'],
      defaultNetwork: 'ERC20'
    },
    ETH: { 
      name: 'Ethereum', 
      color: 'from-purple-500 to-indigo-500',
      networks: ['ERC20'],
      defaultNetwork: 'ERC20'
    },
    BNB: {
      name: 'Binance Coin',
      color: 'from-yellow-500 to-amber-500',
      networks: ['BEP20'],
      defaultNetwork: 'BEP20'
    },
    DOGE: {
      name: 'Dogecoin',
      color: 'from-yellow-600 to-amber-600',
      networks: ['DOGE'],
      defaultNetwork: 'DOGE'
    },
    TRX: {
      name: 'Tron',
      color: 'from-red-500 to-pink-500',
      networks: ['TRC20'],
      defaultNetwork: 'TRC20'
    },
    XRP: {
      name: 'Ripple',
      color: 'from-blue-600 to-purple-600',
      networks: ['XRP'],
      defaultNetwork: 'XRP'
    },
    BDX: {
      name: 'Beldex',
      color: 'from-purple-500 to-indigo-500',
      networks: ['BDX'],
      defaultNetwork: 'BDX'
    }
  };

  const networkInfo = {
    'ERC20': { fee: '1-5 USDT', time: '2-5 minutes', name: 'Ethereum Network' },
    'TRC20': { fee: '0-1 USDT', time: '1-3 minutes', name: 'Tron Network' },
    'BEP20': { fee: '0.1-1 USDT', time: '1-3 minutes', name: 'Binance Smart Chain' },
    'BTC': { fee: '0.0005 BTC', time: '10-30 minutes', name: 'Bitcoin Network' },
    'XRP': { fee: '0.1 XRP', time: '1-5 minutes', name: 'Ripple Network' },
    'DOGE': { fee: '1 DOGE', time: '1-5 minutes', name: 'Dogecoin Network' },
    'BDX': { fee: '0.1 BDX', time: '1-5 minutes', name: 'Beldex Network' }
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        const userData = JSON.parse(localStorage.getItem('user'));
        
        if (!token || !userData) {
          throw new Error('Authentication required');
        }

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Email': userData.email,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch profile data');
        }

        const data = await response.json();
        setUser(prev => ({
          ...prev,
          name: data.name,
          email: data.email,
          phone: data.phone,
          country: data.country,
          currency: data.currency,
          verificationStatus: data.verificationStatus,
          paymentStatus: data.kycStatus || 'pending',
          verificationFee: data.kycAmount || 50
        }));

        setFormData({
          phone: data.phone,
          country: data.country,
          countryCode: data.countryCode || 'US'
        });

      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfileData();
  }, []);

  const fetchWalletAddress = async (selectedCurrency, selectedNetwork) => {
    try {
      setLoadingWallet(true);
      setFetchError('');
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user'));
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/deposit/generate-address`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Email': userData.email,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currency: selectedCurrency,
          network: selectedNetwork
        })
      });

      const data = await response.json();

      if (data.success) {
        setWalletAddress(data.address);
      } else {
        throw new Error(data.message || 'Failed to fetch wallet address');
      }
    } catch (error) {
      console.error('Error fetching wallet address:', error);
      setFetchError(error.message);
      setWalletAddress('');
    } finally {
      setLoadingWallet(false);
    }
  };

  useEffect(() => {
    if (paymentModalOpen && currency && network) {
      fetchWalletAddress(currency, network);
    }
  }, [paymentModalOpen, currency, network]);

  const checkPaymentStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user'));
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/payment/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Email': userData.email,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUser(prev => ({
          ...prev,
          paymentStatus: data.status
        }));
        
        if (data.status === 'completed') {
          setPaymentModalOpen(false);
          setVerificationModalOpen(true);
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const modalStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      transform: 'translate(-50%, -50%)',
      width: '90%',
      maxWidth: '500px',
      backgroundColor: '#1F2937',
      border: 'none',
      borderRadius: '16px',
      padding: '0',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
    },
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      backdropFilter: 'blur(8px)',
      zIndex: 1000
    }
  };

  const openEditModal = () => {
    setIsModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user'));
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Email': userData.email,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phone: formData.phone,
          country: formData.country,
          countryCode: formData.countryCode
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      setUser(prev => ({
        ...prev,
        phone: formData.phone,
        country: formData.country,
      }));
      
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUser(prev => ({ ...prev, profileImage: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleIdCardUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIdCard(file);
    }
  };

  const submitVerification = async () => {
    try {
      const token = localStorage.getItem('token');
      const userData = JSON.parse(localStorage.getItem('user'));
      
      const formData = new FormData();
      formData.append('idCard', idCard);
      
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/verification/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Email': userData.email,
        },
        body: formData
      });

      if (response.ok) {
        setUser(prev => ({ ...prev, verificationStatus: 'Pending Verification' }));
        setVerificationModalOpen(false);
        setIdCard(null);
      } else {
        throw new Error('Failed to submit verification');
      }
    } catch (error) {
      console.error('Error submitting verification:', error);
    }
  };

  const handleVerifyClick = () => {
    if (user.paymentStatus !== 'completed') {
      setPaymentModalOpen(true);
    } else {
      setVerificationModalOpen(true);
    }
  };

  const handlePaymentComplete = async () => {
    setIsProcessingPayment(true);
    setTimeout(() => {
      checkPaymentStatus();
      setIsProcessingPayment(false);
    }, 2000);
  };

  const copyToClipboard = () => {
    if (walletAddress) {
      navigator.clipboard.writeText(walletAddress);
      setCopiedAddress(currency);
      setTimeout(() => setCopiedAddress(''), 2000);
    }
  };

  const handleCurrencyChange = (newCurrency) => {
    setCurrency(newCurrency);
    const config = currencyConfig[newCurrency];
    if (config) {
      setNetwork(config.defaultNetwork);
    }
  };

  const getPaymentStatusIcon = () => {
    switch(user.paymentStatus) {
      case 'completed':
        return <FaCheckCircle className="text-green-400 mr-2" />;
      case 'pending':
        return <FaSpinner className="animate-spin text-yellow-400 mr-2" />;
      case 'failed':
        return <FaTimes className="text-red-400 mr-2" />;
      default:
        return <FaExclamationTriangle className="text-gray-400 mr-2" />;
    }
  };

  const getPaymentStatusText = () => {
    switch(user.paymentStatus) {
      case 'completed':
        return 'Payment Completed';
      case 'pending':
        return 'Payment Pending';
      case 'failed':
        return 'Payment Failed';
      default:
        return 'Payment Required';
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white pb-20">
        <div className="flex-1 flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
        <Nav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white pb-20">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-lg border-b border-gray-700 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => window.history.back()}
              className="p-2 hover:bg-gray-700/50 rounded-xl transition-all duration-200"
            >
              <i className="fas fa-arrow-left text-lg"></i>
            </button>
            <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Profile Settings
            </h1>
            <div className="w-8"></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 max-w-lg">
        <div className="space-y-6">
          {/* Profile Card */}
          <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
            <div className="flex items-center mb-6">
              <div className="relative">
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 rounded-full mr-4 shadow-lg">
                  {previewImage ? (
                    <img 
                      src={previewImage} 
                      alt="Profile" 
                      className="w-16 h-16 rounded-full object-cover"
                    />
                  ) : (
                    <FaUser className="text-2xl text-white" />
                  )}
                </div>
                <label className="absolute -bottom-1 -right-1 bg-gray-700 p-2 rounded-full cursor-pointer shadow-lg border border-gray-600">
                  <FaCamera className="text-sm" />
                  <input 
                    type="file" 
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">{user.name}</h3>
                <p className="text-gray-400">{user.email}</p>
              </div>
            </div>
            
            {/* Profile Details */}
            <div className="bg-gray-700/30 border border-gray-600/50 rounded-2xl p-6 mb-6 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="flex justify-between pb-4 border-b border-gray-600/50">
                  <span className="text-gray-400">Phone:</span>
                  <span className="font-medium">{user.phone}</span>
                </div>
                <div className="flex justify-between pb-4 border-b border-gray-600/50">
                  <span className="text-gray-400">Country:</span>
                  <span className="font-medium">{user.country}</span>
                </div>
                <div className="flex justify-between pb-4 border-b border-gray-600/50">
                  <span className="text-gray-400">Currency:</span>
                  <span className="font-medium">{user.currency}</span>
                </div>
                <div className="flex justify-between items-center pb-4 border-b border-gray-600/50">
                  <span className="text-gray-400">Verification Payment:</span>
                  <div className="flex items-center">
                    <span className={`flex items-center ${user.paymentStatus === 'completed' ? 'text-green-400' : user.paymentStatus === 'pending' ? 'text-yellow-400' : user.paymentStatus === 'failed' ? 'text-red-400' : 'text-gray-400'}`}>
                      {getPaymentStatusIcon()}
                      {getPaymentStatusText()}
                    </span>
                    {user.paymentStatus !== 'completed' && (
                      <button 
                        onClick={handleVerifyClick}
                        className="ml-2 text-xs bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 px-3 py-1 rounded-lg transition-all duration-200"
                      >
                        Pay Now
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Verification:</span>
                  <div className="flex items-center">
                    <span className={`${user.verificationStatus === 'Verified' ? 'text-green-400' : user.verificationStatus === 'Pending Verification' ? 'text-yellow-400' : 'text-red-400'}`}>
                      {user.verificationStatus}
                    </span>
                    {user.verificationStatus !== 'Verified' && user.paymentStatus === 'completed' && (
                      <button 
                        onClick={() => setVerificationModalOpen(true)}
                        className="ml-2 text-xs bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 px-3 py-1 rounded-lg transition-all duration-200"
                      >
                        Verify
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <button 
              onClick={openEditModal}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 py-4 px-6 rounded-xl font-bold transition-all duration-200 shadow-lg shadow-blue-500/25"
            >
              Edit Profile
            </button>
          </div>
        </div>
      </main>

      {/* Edit Profile Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        style={modalStyles}
        contentLabel="Edit Profile"
      >
        <div className="p-6 bg-gray-800 rounded-2xl shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">Edit Profile</h2>
            <button 
              onClick={() => setIsModalOpen(false)}
              className="text-gray-400 hover:text-white transition p-2 hover:bg-gray-700/50 rounded-xl"
            >
              <FaTimes size={20} />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-gray-400 mb-2 font-medium">Phone Number</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-3 bg-gray-700 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-500 border border-gray-600"
                placeholder="Enter phone number"
                required
              />
            </div>

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 py-4 rounded-xl font-bold transition-all duration-200 shadow-lg shadow-blue-500/25"
            >
              Save Changes
            </button>
          </form>
        </div>
      </Modal>

      {/* Payment Modal */}
      <Modal
        isOpen={paymentModalOpen}
        onRequestClose={() => setPaymentModalOpen(false)}
        style={modalStyles}
        contentLabel="Verification Payment"
      >
        <div className="p-6 bg-gray-800 rounded-2xl shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">Verification Payment</h2>
            <button 
              onClick={() => setPaymentModalOpen(false)}
              className="text-gray-400 hover:text-white transition p-2 hover:bg-gray-700/50 rounded-xl"
            >
              <FaTimes size={20} />
            </button>
          </div>

          <div className="space-y-5">
            <p className="text-gray-300">
              To complete your verification, please pay the verification fee of{" "}
              <span className="font-bold text-white">${user.verificationFee}</span>.
            </p>

            <div className="bg-gray-700/50 border border-gray-600/50 rounded-2xl p-5 space-y-4 backdrop-blur-sm">
              <h3 className="flex items-center font-semibold text-white mb-2">
                <FaMoneyBillWave className="mr-2 text-green-400" /> Payment Wallet Address
              </h3>

              {/* Currency Selector */}
              <div>
                <label className="block text-gray-300 mb-1">Currency</label>
                <select
                  value={currency}
                  onChange={(e) => handleCurrencyChange(e.target.value)}
                  className="w-full bg-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 backdrop-blur-sm border border-gray-600"
                >
                  {Object.entries(currencyConfig).map(([key, config]) => (
                    <option key={key} value={key}>{config.name} ({key})</option>
                  ))}
                </select>
              </div>

              {/* Network Selector */}
              {currencyConfig[currency]?.networks.length > 1 && (
                <div>
                  <label className="block text-gray-300 mb-1">Network</label>
                  <select
                    value={network}
                    onChange={(e) => setNetwork(e.target.value)}
                    className="w-full bg-gray-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 backdrop-blur-sm border border-gray-600"
                  >
                    {currencyConfig[currency].networks.map((net) => (
                      <option key={net} value={net}>{net}</option>
                    ))}
                  </select>
                </div>
              )}

              {fetchError && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3">
                  <p className="text-red-400 text-sm">{fetchError}</p>
                  <button 
                    onClick={() => fetchWalletAddress(currency, network)}
                    className="mt-2 text-xs bg-red-500 hover:bg-red-600 px-3 py-1 rounded-lg transition-colors"
                  >
                    Retry
                  </button>
                </div>
              )}

              {loadingWallet ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                walletAddress && (
                  <>
                    {/* Wallet Address Box */}
                    <div className="bg-gray-900/50 border border-gray-600 rounded-xl p-4 relative backdrop-blur-sm">
                      <code className="break-all text-gray-100 text-sm">
                        {walletAddress}
                      </code>
                      <button 
                        className="absolute top-3 right-3 text-xs bg-gray-600 hover:bg-gray-500 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1"
                        onClick={copyToClipboard}
                      >
                        {copiedAddress === currency ? (
                          <>
                            <FaCheckCircle className="text-green-400" />
                            <span className="text-green-400">Copied!</span>
                          </>
                        ) : (
                          <>
                            <FaCopy />
                            <span>Copy</span>
                          </>
                        )}
                      </button>
                    </div>

                    {/* QR Code */}
                    <div className="bg-white p-4 rounded-2xl inline-block max-w-[200px] shadow-lg mx-auto block">
                      <img
                        src={`https://api.qrserver.com/v1/create-qr-code/?data=${walletAddress}&size=200x200&margin=10`}
                        alt="Deposit QR Code"
                        className="w-full h-auto rounded-lg"
                      />
                    </div>

                    <p className="text-sm text-gray-400 text-center">
                      Send exactly <span className="font-medium">${user.verificationFee}</span> equivalent in {currency} to this address. 
                      The verification process will begin after your payment is confirmed.
                    </p>
                  </>
                )
              )}
            </div>

            {/* Network Info */}
            {network && networkInfo[network] && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <i className="fas fa-info-circle text-blue-400 mt-1"></i>
                  <div className="text-sm text-blue-300">
                    <p className="font-semibold">{networkInfo[network].name}</p>
                    <p className="mt-1">
                      Estimated Time: {networkInfo[network].time}
                      {networkInfo[network].fee && ` • Fee: ${networkInfo[network].fee}`}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={handlePaymentComplete}
              disabled={isProcessingPayment || !walletAddress}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-green-500/25"
            >
              {isProcessingPayment ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </div>
              ) : (
                'I Have Sent the Payment'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Verification Modal */}
      <Modal
        isOpen={verificationModalOpen}
        onRequestClose={() => setVerificationModalOpen(false)}
        style={modalStyles}
        contentLabel="ID Verification"
      >
        <div className="p-6 bg-gray-800 rounded-2xl shadow-xl">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-white">ID Verification</h2>
            <button 
              onClick={() => setVerificationModalOpen(false)}
              className="text-gray-400 hover:text-white transition p-2 hover:bg-gray-700/50 rounded-xl"
            >
              <FaTimes size={20} />
            </button>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-400">
              Please upload a clear photo of your government-issued ID (Passport, Driver's License, or National ID)
            </p>
            
            <div className="border-2 border-dashed border-gray-600 rounded-2xl p-6 text-center backdrop-blur-sm hover:border-gray-500 transition-colors">
              <label className="cursor-pointer">
                <div className="flex flex-col items-center justify-center">
                  <FaCamera className="text-3xl mb-2 text-gray-400" />
                  <p className="text-gray-400">
                    {idCard ? idCard.name : 'Click to upload ID document'}
                  </p>
                  {idCard && (
                    <p className="text-green-400 text-sm mt-2 flex items-center">
                      <FaCheckCircle className="mr-1" /> File selected
                    </p>
                  )}
                </div>
                <input 
                  type="file" 
                  accept="image/*,.pdf"
                  onChange={handleIdCardUpload}
                  className="hidden"
                />
              </label>
            </div>
            
            <button
              onClick={submitVerification}
              disabled={!idCard}
              className={`w-full py-4 px-6 rounded-xl font-bold transition-all duration-200 ${
                idCard 
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg shadow-blue-500/25' 
                  : 'bg-gray-600 cursor-not-allowed'
              }`}
            >
              Submit Verification
            </button>
          </div>
        </div>
      </Modal>

      {/* Bottom Navigation */}
      <Nav />

      {/* Include Font Awesome */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
    </div>
  );
};

export default Profile;